import React from 'react';
import { Link } from 'react-router-dom';
import { useCurrency } from '../context/CurrencyContext';
import { getCategoryColor } from '../utils/helpers';
import { Car, Palette, Wine, Gem, ShoppingBag, Watch, Hexagon, KeyRound, ShieldCheck, ShieldAlert, ShieldOff, CalendarClock } from 'lucide-react';
import { FEATURES } from '../config/features';

const iconMap = {
  car: Car,
  art: Palette,
  wine: Wine,
  jewelry: Gem,
  handbag: ShoppingBag,
  watch: Watch,
};

const insuranceState = (asset) => {
  const insured = Number(asset.insured_value || 0);
  const cv = Number(asset.current_value || 0);
  if (!insured) return { tone: 'red', label: 'No cover', Icon: ShieldOff };
  if (cv > insured * 1.05) return { tone: 'amber', label: 'Underinsured', Icon: ShieldAlert };
  return { tone: 'emerald', label: 'Insured', Icon: ShieldCheck };
};

const daysUntil = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return Math.floor((d.getTime() - Date.now()) / 86400000);
};

const insuranceToneClasses = {
  emerald: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/25',
  amber:   'bg-amber-500/15 text-amber-400 border-amber-500/25',
  red:     'bg-red-500/15 text-red-400 border-red-500/25',
};

export const AssetCard = ({ asset, totalPortfolio = 0 }) => {
  const { formatCurrency } = useCurrency();
  const Icon = iconMap[asset.category] || Palette;
  const gain = asset.current_value - asset.purchase_price;
  const gainPercent = ((gain / asset.purchase_price) * 100).toFixed(1);
  const isPositive = gain >= 0;

  const ins = insuranceState(asset);
  const InsIcon = ins.Icon;
  const expDays = daysUntil(asset.insurance_expiry);
  const portfolioPct = totalPortfolio > 0 ? ((asset.current_value / totalPortfolio) * 100) : null;

  return (
    <Link
      to={`/assets/${asset.id}`}
      data-testid={`asset-card-${asset.id}`}
      className="group block luxury-card p-6 hover-gold-glow"
    >
      <div className="relative aspect-[4/3] mb-4 overflow-hidden bg-[#0A0A0A]">
        {asset.image_url ? (
          <img
            src={asset.image_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${asset.image_url}` : asset.image_url}
            alt={asset.name}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Icon size={48} className={`${getCategoryColor(asset.category)} opacity-50`} />
          </div>
        )}
        <div className="absolute inset-0 gradient-overlay opacity-60" />
        <div className="absolute top-3 left-3">
          <span className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs uppercase tracking-wider bg-black/60 backdrop-blur-sm ${getCategoryColor(asset.category)}`}>
            <Icon size={12} />
            {asset.category}
          </span>
        </div>
        {FEATURES.TOKENISATION_ENABLED && asset.token_status && (
          <div className="absolute top-3 right-3">
            <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded backdrop-blur-sm ${
              asset.token_status === 'listed' ? 'bg-[#627EEA]/80 text-white' :
              asset.token_status === 'minted' ? 'bg-emerald-500/80 text-white' :
              asset.token_status === 'verification_pending' ? 'bg-amber-500/80 text-white' :
              'bg-amber-500/80 text-white'
            }`}>
              <Hexagon size={10} />
              {asset.token_status === 'listed' ? 'Listed' :
               asset.token_status === 'minted' ? 'Minted' :
               asset.token_status === 'verification_pending' ? 'Verifying' :
               'Pending'}
            </span>
          </div>
        )}
        {FEATURES.TOKENISATION_ENABLED && !asset.token_status && (
          <div className="absolute top-3 right-3">
            <span className="inline-flex items-center gap-1 px-2 py-1 text-[10px] rounded backdrop-blur-sm bg-gold/80 text-black uppercase tracking-wider golden-key-shimmer" data-testid="golden-key-badge">
              <KeyRound size={10} />
              Eligible
            </span>
          </div>
        )}
        {/* Insurance status pill (always shown - risk-first product) */}
        <div className="absolute bottom-3 left-3 flex flex-wrap gap-1.5">
          <span className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-wider rounded border backdrop-blur-sm ${insuranceToneClasses[ins.tone]}`} data-testid={`insurance-${asset.id}`}>
            <InsIcon size={10} />
            {ins.label}
          </span>
          {expDays !== null && expDays >= 0 && expDays <= 90 && (
            <span className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-wider rounded border backdrop-blur-sm ${
              expDays <= 30 ? insuranceToneClasses.red : insuranceToneClasses.amber
            }`} data-testid={`expiry-${asset.id}`}>
              <CalendarClock size={10} />
              {expDays === 0 ? 'Expires today' : `${expDays}d to renew`}
            </span>
          )}
        </div>
        {/* % of portfolio (top-left under category) */}
        {portfolioPct !== null && portfolioPct > 0 && (
          <div className="absolute bottom-3 right-3">
            <span className="inline-flex items-center px-2 py-1 text-[10px] rounded bg-black/60 backdrop-blur-sm text-gold border border-gold/25" data-testid={`portfolio-pct-${asset.id}`}>
              {portfolioPct.toFixed(1)}% of portfolio
            </span>
          </div>
        )}
      </div>

      <h3 className="font-serif text-lg text-white mb-2 group-hover:text-gold transition-colors">
        {asset.name}
      </h3>

      <div className="flex items-end justify-between">
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Current Value</p>
          <p className="text-xl font-semibold text-white">{formatCurrency(asset.current_value)}</p>
        </div>
        <div className="text-right">
          <p className={`text-sm font-medium ${isPositive ? 'text-emerald-400' : 'text-red-400'}`}>
            {isPositive ? '+' : ''}{formatCurrency(gain)}
          </p>
          <p className={`text-xs ${isPositive ? 'text-emerald-400' : 'text-red-400'}`}>
            {isPositive ? '+' : ''}{gainPercent}%
          </p>
        </div>
      </div>
    </Link>
  );
};
